// src/pages/SoldUnityCard.js
import React, { useEffect, useState } from "react";
import { useAuthCheck } from "../components/Auth";
import { api } from "../components/Api";
import Loading from "../components/Loading";
import * as XLSX from "xlsx";

function formatDate(dateStr) {
    if (!dateStr) return "";
    const date = new Date(dateStr);
    return date.toLocaleString("fr-FR", {
        year: "numeric",
        month: "2-digit",
        day: "2-digit",
    });
}

export default function SoldUnityCard() {
    const [month, setMonth] = useState(() => {
        const now = new Date();
        return `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, "0")}`;
    });
    const [soldCards, setSoldCards] = useState([]);
    const [qualities, setQualities] = useState([]);
    const [loading, setLoading] = useState(true);
    const { checkToken } = useAuthCheck();

    useEffect(() => { checkToken(); }, [checkToken]);

    // 🔹 Fetch des cartes vendues
    useEffect(() => {
        const fetchSoldCards = async () => {
            setLoading(true);
            try {
                const res = await api.get(`/solded-cards?month=${month}`);
                setSoldCards(res.data);
            } catch (err) {
                console.error(err);
                alert("Erreur lors de la récupération des cartes vendues.");
            } finally {
                setLoading(false);
            }
        };

        fetchSoldCards();
    }, [month]);

    useEffect(() => {
        const fetchQualities = async () => {
            try {
                const res = await api.get('/quality-cards');
                setQualities(res.data);
            } catch (err) {
                console.error(err);
            }
        };
        fetchQualities();
    }, []);

    // 🔹 Préparer les données pour Excel
    const prepareSheetData = (data) => {
        const sheetData = data.map(c => ({
            "Référence": c.unity_card?.reference || "",
            "Licence": c.unity_card?.license_card?.license_card || "",
            "Collection": c.unity_card?.collection?.collection_name || "",
            "Extension": c.unity_card?.extension?.extension_name || "",
            "Type": c.unity_card?.booster ? "Booster" : "Carte Unique",
            "Nom": c.unity_card?.name || "",
            "Langue": c.unity_card?.language?.language_name || "",
            "Qualité": qualities.find(q => q.id === c.quality_id)?.quality_card_name || "-",
            "Prix de vente": c.sell || 0,
            "Date de vente": formatDate(c.updated_at),
        }));

        // Totaux
        const totalSell = sheetData.reduce((acc, row) => acc + Number(row["Prix de vente"] || 0), 0);

        sheetData.push({
            "Référence": "",
            "Licence": "",
            "Collection": "",
            "Extension": "",
            "Type": "",
            Nom: "Total",
            "Qualité": "",
            "Langue": "",
            "Prix de vente": totalSell.toFixed(2),
            "Date de vente": "",
        });

        return sheetData;
    };


    const downloadExcel = () => {
        if (!soldCards.length) return alert("Aucune carte vendue ce mois.");

        const wb = XLSX.utils.book_new();
        const wsAll = XLSX.utils.json_to_sheet(prepareSheetData(soldCards));
        XLSX.utils.book_append_sheet(wb, wsAll, "Cartes et Boosters");

        // Onglets par type de carte : Boost / Unique
        const types = [...new Set(soldCards.map(c => c.unity_card?.booster ? "Booster" : "Carte Unique"))];

        types.forEach(typeName => {
            const typeData = soldCards.filter(c => (c.unity_card?.booster ? "Booster" : "Carte Unique") === typeName);
            const ws = XLSX.utils.json_to_sheet(prepareSheetData(typeData));
            // Le nom d'un onglet ne peut pas dépasser 31 caractères
            XLSX.utils.book_append_sheet(wb, ws, typeName.substring(0, 31));
        });

        XLSX.writeFile(wb, `ventes_cartes_${month}.xlsx`);
    };

    return (
        <div className="container mx-auto p-4 space-y-4">
            <h1 className="text-2xl font-bold dark:text-white">Cartes vendues</h1>

            <div className="flex space-x-2 items-center">
                <label>Mois :</label>
                <input
                    type="month"
                    value={month}
                    onChange={e => setMonth(e.target.value)}
                    className="border p-2 rounded dark:bg-gray-700 bg-white"
                />
                {localStorage.getItem("role") <= 2 && (
                    <button
                        onClick={downloadExcel}
                        className="bg-green-500 text-white px-4 py-2 rounded hover:bg-green-600"
                    >
                        Télécharger Excel
                    </button>
                )}
            </div>

            {loading ? (
                <Loading message="Chargement des cartes vendues..." />
            ) : (
                <table className="w-full border-collapse border text-sm">
                    <thead>
                        <tr>
                            <th className="border px-2 py-1">Type</th>
                            <th className="border px-2 py-1">Référence</th>
                            <th className="border px-2 py-1">Licence</th>
                            <th className="border px-2 py-1">Collection</th>
                            <th className="border px-2 py-1">Extension</th>
                            <th className="border px-2 py-1">Nom</th>
                            <th className="border px-2 py-1">Prix de vente</th>
                            <th className="border px-2 py-1">Qualité</th>
                            <th className="border px-2 py-1">Langue</th>
                            <th className="border px-2 py-1">Date</th>
                        </tr>
                    </thead>
                    <tbody>
                        {soldCards.length > 0 ? (
                            soldCards.map((c, index) => {
                                const textColor = "dark:text-red-300 text-red-700";
                                const bgColor = index % 2 === 0 ? "dark:bg-gray-600 bg-gray-050" : "dark:bg-gray-700 bg-gray-100";
                                return (
                                    <tr key={c.id} className={`${bgColor} ${textColor}`}>
                                        <td className="border px-2 py-1">{c.unity_card?.booster ? "Booster" : "Carte Unique"}</td>
                                        <td className="border px-2 py-1">{c.unity_card?.reference || ""}</td>
                                        <td className="border px-2 py-1">{c.unity_card?.license_card?.license_card || ""}</td>
                                        <td className="border px-2 py-1">{c.unity_card?.collection?.collection_name || ""}</td>
                                        <td className="border px-2 py-1">{c.unity_card?.extension?.extension_name || ""}</td>
                                        <td className="border px-2 py-1">{c.unity_card?.name || ""}</td>
                                        <td className="border px-2 py-1">{c.sell || 0}</td>
                                        <td className="border px-2 py-1">
                                            {qualities.find(q => q.id === c.quality_id)?.quality_card_name || "-"}
                                        </td>
                                        <td className="border px-2 py-1">{c.unity_card?.language?.language_name || ""}</td>
                                        <td className="border px-2 py-1">{formatDate(c.updated_at)}</td>
                                    </tr>
                                );
                            })
                        ) : (
                            <tr>
                                <td colSpan={9} className="text-center p-2">
                                    Aucune carte vendue ce mois
                                </td>
                            </tr>
                        )}
                    </tbody>
                </table>
            )}
        </div>
    );
}
